/* eslint @typescript-eslint/camelcase: 0 */
import apiFetch from '../core/apiFetch';
import { checkStatus, handleError } from '../../utils/api';
import { getAuthTokenAsync } from '../../utils/authToken';
import {
  SimplePatient,
  SimplePatientApi,
  RegisterError,
} from '../../interfaces/register';
import { VALIDATION_ERROR } from '../../constants/register';
import { formatDate } from '../../utils/shared/normalize';

const mapSimplePatientApi = ({
  acceptTerms,
  dobDay,
  dobMonth,
  dobYear,
  ...rest
}: SimplePatient): SimplePatientApi => {
  return {
    terms_of_service: acceptTerms,
    birthdate: formatDate(`${dobMonth}/${dobDay}/${dobYear}`, 'iso'),
    ...rest,
  };
};

const createSimplePatientAsync = async (
  values: SimplePatient
): Promise<{ error?: RegisterError; simplePatient?: { token: string } }> => {
  const url = `api/v2/simple_patients`;

  try {
    const authToken = await getAuthTokenAsync();

    const response = await apiFetch(authToken, url, {
      method: 'POST',
      body: JSON.stringify({ simple_patient: mapSimplePatientApi(values) }),
    });

    await checkStatus(response);

    const { simple_patient: simplePatient } = await response.json();
    return {
      error: undefined,
      simplePatient: { token: simplePatient.token },
    };
  } catch (error) {
    const { response } = error;

    if (response.status === 422) {
      const payload = await response.json();

      const baseError = payload.errors.base;
      const isInvalid =
        baseError &&
        baseError.filter(
          (err) =>
            err === 'DUPLICATE_USER' ||
            err === 'USER_REGISTERED' ||
            err === 'LOCKED_USER'
        ).length;
      if (isInvalid) {
        return {
          simplePatient: undefined,
          error: {
            code: `${baseError[0]}_ERROR`,
            message: payload.errors,
            userId: payload.user_id,
          },
        };
      }

      let usernameError = payload.errors.username;

      if (usernameError && usernameError.length) {
        usernameError = usernameError.map((err) =>
          err.replace('Username', 'Email')
        );
        return {
          simplePatient: undefined,
          error: { code: VALIDATION_ERROR, message: { email: usernameError } },
        };
      }

      return {
        simplePatient: undefined,
        error: { code: VALIDATION_ERROR, message: payload.errors },
      };
    }

    handleError('unexpected status - create simple patient', error);
    return { error, simplePatient: undefined };
  }
};

export default createSimplePatientAsync;
